import clsx from 'clsx';
import {
  type KeyboardEvent,
  forwardRef,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import styles from './EditableText.module.css';
import { useIsTextOverflowing } from './useIsTextOverflowing';

export interface EditableTextHandle {
  /**
   * Opens the field with its text selected. Call it only after the opener has
   * released focus — from a Menu: in onExitTransitionEnd.
   */
  startEditing: () => void;
}

export interface EditableTextProps {
  /** The editable text. Rendered as plain text until the field is opened. */
  value: string;
  /**
   * Trailing text pinned outside the `<input>` — never editable or selectable,
   * re-attached on commit (e.g. a file extension).
   */
  suffix?: string;
  /** Empty, unchanged and refused drafts never reach it. */
  onCommit: (value: string) => void;
  /**
   * The message to show while the draft (with `suffix`) is invalid; commits are
   * refused meanwhile. Must accept this component's own value.
   */
  getDraftError?: (value: string) => string | undefined;
  /** Accessible name for the field, which carries no visible label. */
  editLabel: string;
  /** Notified when the field opens and closes, for layout that has to react. */
  onEditingChange?: (isEditing: boolean) => void;
  /** Put typography here: both states inherit it, so they cannot drift. */
  className?: string;
}

/**
 * Text that becomes an inline field on demand, with a pinned uneditable suffix.
 * Opening is imperative (`EditableTextHandle.startEditing`); the rest is owned here.
 */
export const EditableText = forwardRef<EditableTextHandle, EditableTextProps>(
  (
    {
      value,
      suffix = '',
      onCommit,
      getDraftError,
      editLabel,
      onEditingChange,
      className,
    },
    ref
  ) => {
    /** Non-null only while editing, so it doubles as the open/closed flag. */
    const [draft, setDraft] = useState<string | null>(null);
    const isEditing = draft !== null;
    /** Set by Escape so the blur that follows discards instead of saving. */
    const isCancelledRef = useRef(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const draftErrorId = useId();
    const { ref: textRef, isOverflowing } = useIsTextOverflowing(value);

    useImperativeHandle(ref, () => ({
      startEditing: () => {
        setDraft(value);
        onEditingChange?.(true);
      },
    }));

    // Focusing on mount is safe: startEditing runs after the opener let go.
    useLayoutEffect(() => {
      if (!isEditing) {
        return;
      }
      inputRef.current?.focus();
      inputRef.current?.select();
      // On open only — re-running per keystroke would reselect the text.
    }, [isEditing]);

    const trimmedDraft = draft?.trim() ?? '';
    const draftError =
      isEditing && trimmedDraft !== ''
        ? getDraftError?.(`${trimmedDraft}${suffix}`)
        : undefined;

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter' && draftError) {
        // Stay open next to the message; Escape still cancels.
        event.preventDefault();
        return;
      }
      if (event.key === 'Enter' || event.key === 'Escape') {
        event.preventDefault();
        isCancelledRef.current = event.key === 'Escape';
        event.currentTarget.blur();
      }
    };

    const handleBlur = () => {
      const isCancelled = isCancelledRef.current;
      isCancelledRef.current = false;
      const next = draft?.trim() ?? '';
      setDraft(null);
      onEditingChange?.(false);

      if (isCancelled || !next) {
        return;
      }

      const nextValue = `${next}${suffix}`;
      const wholeValue = `${value}${suffix}`;

      // draftError was computed for this exact draft on the last render.
      if (nextValue === wholeValue || draftError) {
        return;
      }

      onCommit(nextValue);
    };

    return (
      <div
        className={clsx(styles.root, className)}
        data-editing={isEditing || undefined}
      >
        {isEditing ? (
          <>
            <input
              className={styles.input}
              value={draft}
              // Sizing fallback for browsers without `field-sizing: content`.
              size={Math.max(draft.length, 1)}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              aria-label={editLabel}
              aria-invalid={Boolean(draftError) || undefined}
              aria-describedby={draftError ? draftErrorId : undefined}
              ref={inputRef}
            />
            {draftError && (
              <span
                id={draftErrorId}
                role="alert"
                className={styles.draftError}
              >
                {draftError}
              </span>
            )}
          </>
        ) : (
          <span ref={textRef} className={styles.text}>
            {value}
          </span>
        )}
        {suffix && (
          <span
            className={clsx(
              styles.suffix,
              !isEditing && isOverflowing && styles.suffixSpaced
            )}
          >
            {suffix}
          </span>
        )}
      </div>
    );
  }
);

EditableText.displayName = 'EditableText';
